import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import authService from "../../firebase/authService";
import dbService from "../../firebase/dbService";
import { Timestamp } from "firebase/firestore";


export const register = createAsyncThunk(
  "auth/register",
  async (
    { email, password, name, docId, data, photoUrl },
    { rejectWithValue }
  ) => {
    try {
      // 1. Create a new auth in firebase auth
      const user = await authService.createAccount({
        email,
        password,
        name,
        photoUrl,
      });


      //2. add user auth data to firestore
      const registerData = {
        ...data,
        createdAt: Timestamp.now(),
        modifiedAt: Timestamp.now(),
        uid: user.uid,
      };

      // Create master user metadata document
      const userMeta = {
        uid: user.uid,
        email: user.email || "",
        accountType: data.accountType,
        createdAt: Timestamp.now(),
      };
      await dbService.createDocumentWithId("user", userMeta, user.uid);

      // Destructure to remove accountType from the profile data
      const { accountType, ...roleData } = registerData;

      const roleCollection = (data.accountType === "child" || data.accountType === "student") ? "students" : `${data.accountType}s`;
      const created = await dbService.createDocumentWithId(
        roleCollection,
        roleData,
        user.uid
      );
      if (!created) {
        // If Firestore write failed, roll back auth user
        try {
          await authService.deleteCurrentUser();
        } catch (_) {
          // ignore secondary rollback errors
        }
        throw new Error("Failed to save user profile. Please try again.");
      }

      //3. get the current user (registered now) details from firestore
      const userAuth = await authService.getCurrentUser();
      
      let profileData = null;
      if (data.accountType === "parent") {
        profileData = await dbService.getAuthDetails('parents', userAuth.uid);
      } else if (data.accountType === "school") {
        profileData = await dbService.getAuthDetails('schools', userAuth.uid);
      } else if (data.accountType === "child" || data.accountType === "student") {
        profileData = await dbService.getAuthDetails('students', userAuth.uid).catch(() => null) ||
                      await dbService.getAuthDetails('children', userAuth.uid).catch(() => null);
      }

      const userData = {
        uid: userAuth.uid,
        email: userAuth.email,
        name: userAuth.displayName || profileData?.name || "",
        phone: data?.phone || "",
        accountType: data?.accountType || "",
        ...profileData
      };

      let registeredSchoolDetails;
      if (data?.accountType === 'parent') {
        const schoolId = data?.schoolId;
        if (schoolId) {
          const schoolData = await dbService.getdocument('schools', schoolId).catch(() => null);
          if (schoolData) {
            registeredSchoolDetails = {
              schoolId: schoolData.id,
              schoolName: schoolData.schoolName,
              schoolAddress: schoolData.schoolAddress,
              schoolLogoUrl: schoolData.schoolLogoUrl,
              fee: schoolData.fee?.total || 0
            }
          }
        }
      }

      return { userData, registeredSchoolDetails };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const login = createAsyncThunk(
  "auth/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      let userAuth = await authService.getCurrentUser();

      if (!userAuth) {
        try {
          userAuth = await authService.login({ email, password });
        } catch (error) {
          throw new Error(error.message);
        }
      }

      let data = null;

      // 1. Point lookup in unified 'user' master collection
      const userMeta = await dbService.getdocument("user", userAuth.uid).catch(() => null);
      if (userMeta && userMeta.accountType) {
        const role = userMeta.accountType === "child" ? "student" : userMeta.accountType;
        const roleCollection = role === "student" ? "students" : `${role}s`;
        let details = await dbService.getdocument(roleCollection, userAuth.uid).catch(() => null);
        if (!details && role === "student") {
          // Check legacy children collection if not found in students
          details = await dbService.getdocument("children", userAuth.uid).catch(() => null);
        }
        if (details) {
          data = { ...details, accountType: role };
        }
      }

      // 2. Fallback check collections directly (backward compatibility)
      if (!data) {
        const schoolDetails = await dbService.getAuthDetails("schools", userAuth.uid).catch(() => null);
        if (schoolDetails) {
          data = { ...schoolDetails, accountType: "school" };
        } else {
          const parentDetails = await dbService.getAuthDetails("parents", userAuth.uid).catch(() => null);
          if (parentDetails) {
            data = { ...parentDetails, accountType: "parent" };
          } else {
            const studentDetails = await dbService.getAuthDetails("students", userAuth.uid).catch(() => null);
            if (studentDetails) {
              data = { ...studentDetails, accountType: "student" };
            } else {
              const childDetails = await dbService.getAuthDetails("children", userAuth.uid).catch(() => null);
              if (childDetails) {
                data = { ...childDetails, accountType: "student" };
              }
            }
          }
        }
      }

      if (!data) {
        throw new Error("User not found");
      }

      const userData = {
        uid: userAuth.uid,
        email: userAuth.email,
        name: userAuth.displayName || data.name || "",
        phone: data?.phone || "",
        accountType: data?.accountType || "",
        ...data
      };

      let registeredSchoolDetails;
      if (data?.accountType === 'parent') {
        const schoolId = data?.schoolId;
        if (schoolId) {
          const schoolData = await dbService.getdocument('schools', schoolId).catch(() => null);
          if (schoolData) {
            registeredSchoolDetails = {
              schoolId: schoolData.id,
              schoolName: schoolData.schoolName,
              schoolAddress: schoolData.schoolAddress,
              schoolLogoUrl: schoolData.schoolLogoUrl,
              fee: schoolData.fee?.total || 0
            }
          }
        }
      }
      return { userData, registeredSchoolDetails };
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message);
    }
  }
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      return null;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  userDetails: null,
  schoolDetails: null,
  isAuthenticated: false,
  loading: false,
  error: null,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    updateUserDetails: (state, action) => {
      state.userDetails = {
        ...state.userDetails,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.userDetails = action.payload.userData;
        state.schoolDetails = action.payload.registeredSchoolDetails
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.userDetails = action.payload.userData;
        state.schoolDetails = action.payload.registeredSchoolDetails
        state.isAuthenticated = true;
        state.loading = false;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(logout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.userDetails = null;
        state.schoolDetails = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { updateUserDetails } = authSlice.actions;

export default authSlice.reducer;
